import {
  GenericForm,
  GenericFormFieldWhichIsDebounced,
  GenericFormLogicalField,
  GenericFormLogicalFieldPresentation,
  genericBeforeValidate_removeDefaultKeyedError,
  genericValidate_textField,
  ValidationEvent,
} from "../../../hooks";

export const maxEmailLength = 128;

export const emailRegEx =
  /^([a-z0-9,!\#\$%&'\*\+\/=\?\^_`\{\|\}~-]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z0-9,!\#\$%&'\*\+\/=\?\^_`\{\|\}~-]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*@([a-z0-9-]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z0-9-]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*\.(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]){2,})$/i;

export const isValidEmail = (email: string | null | undefined) =>
  (email || "").match(emailRegEx);

interface EmailAddressFormLogicProps
  extends GenericFormLogicalFieldPresentation {}

export const emailAddressFormLogic = <
  Form extends GenericForm,
  App extends unknown
>(
  props: EmailAddressFormLogicProps = {}
): GenericFormLogicalField<Form, App> => ({
  presentation: props,
  validationEvents: [ValidationEvent.onBlur, ValidationEvent.onSubmit],
  beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
  validate: genericValidate_textField({
    required: props.required,
    trim: true,
    maxLength: maxEmailLength,
    customErrorValidation: (value: any) => {
      if (!isValidEmail(value)) {
        return "Please enter a valid email address.";
      }
      return null;
    },
  }),
});

export interface EmailAddress_WithIsEmailRegisteredAlready_DebouncedField
  extends GenericFormFieldWhichIsDebounced {
  value?: string | null | undefined;
  isEmailRegisteredAlready?: boolean;
}

export const emailAddress_withIsEmailRegisteredAlready_formLogic = <
  Form extends GenericForm,
  App extends unknown
>(
  props: EmailAddressFormLogicProps = {}
): GenericFormLogicalField<
  Form,
  App,
  EmailAddress_WithIsEmailRegisteredAlready_DebouncedField
> => ({
  ...emailAddressFormLogic(props),
  debouncedValidate: async (context) => {
    try {
      const emailAddress = context.formReference.current?.[context.name] as
        | string
        | null
        | undefined;

      const areThereAtLeastThreeCharacters = (emailAddress || "").length >= 3;
      if (areThereAtLeastThreeCharacters) {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");

        const request = new Request("/rest/V1/customers/isEmailAvailable", {
          method: "POST",
          headers,
          body: JSON.stringify({ customerEmail: emailAddress }),
        });

        const isEmailAvailableResponse = await fetch(request);

        const isEmailAvailableResponseText =
          await isEmailAvailableResponse.text();

        const isEmailRegisteredAlready =
          isEmailAvailableResponseText === "false";

        const debouncedField = {
          value: emailAddress,
          isEmailRegisteredAlready,
        };

        context.mutate.withParticularDebouncedValidationResult(
          context,
          debouncedField
        );
      }
    } catch (anyException) {
      console.error(anyException);
    }
  },
  debouncedValidationResultCanBeStale: false,
});
